import {
  Button, FormControl, Grid, InputAdornment, InputLabel, MenuItem, Select,
  SelectChangeEvent, SvgIcon, TextField,
} from '@mui/material';
import PurchaseOrdersTable from './PurchaseOrdersTable';
import { CreatePurchaseOrderHeaderRequest, GetPurchaseOrderHeadersResponse, OrderTypesResponse, SupplierResponse } from '../../../app/services/api/generated';
import { useContext, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { PurchaseOrdersFilterOptions } from '../interfaces/PurchaseOrdersFilterOptions';
import { SearchSm } from '../../../assets';
import DatePickerLocalized from '../../../components/shared/DatePickerLocalized';
import { Section } from '../../../components/ui/Section';
import { UserContext } from '../../../components/shared/useUser';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { handleSearchFieldKeyDown } from '../../../utils';
import CreateAdHocReceiptModal from './CreateAdHocReceiptModal';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import useLogError from '../../../hooks/useLogError';
import { useNavigate } from 'react-router-dom';
import { ConfigurationName } from '../../../enums/ConfigurationName';

const PurchaseOrderBox = () => {
  const defaultSort = 'deliveryDate';
  const defaultTake = 10;
  const defaultDirection = 'desc';

  const { selectedStore, hasPermissionTo } = useContext(UserContext);

  const { getPurchaseOrders, getOrderTypes, getPurchaseOrderSuppliers, getConfiguration, createAdHocPurchaseOrder } = useNswagClient();
  const [purchaseOrders, setPurchaseOrders] = useState<GetPurchaseOrderHeadersResponse>({});
  const [ordersType, setOrdersType] = useState<OrderTypesResponse>({});
  const [supplierNames, setSupplierNames] = useState<SupplierResponse>({});
  const [orderBy, setOrderBy] = useState<string>('deliveryDate');
  const [loading, setLoading] = useState(true);
  const [isAdHocReceiptCreationEnabled, setIsAdHocReceiptCreationEnabled] = useState(false);
  const [isCreateAdHocModalOpen, setIsCreateAdHocModalOpen] = useState(false);
  const [isCreatingAdHocReceipt, setIsCreatingAdHocReceipt] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'error' | 'warning' | 'info' | 'success'>('error');
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);

  const navigate = useNavigate();
  const { logError, extractErrorMessage } = useLogError();

  const [search, setSearch] = useState('');
  const [lastSearch, setLastSearch] = useState('');
  const [orderType, setOrderType] = useState('All');
  const [supplierName, setSupplierName] = useState('All');
  const [received, setReceived] = useState<boolean | undefined>();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  const { t } = useTranslation('common');

  const canCreateAdHocReceipt = hasPermissionTo(['CreateAdHocReceipts']) && isAdHocReceiptCreationEnabled;

  useEffect(() => {
    loadOrderTypes();
    loadSupplierNames();
    loadAdHocReceiptCreationConfiguration();
  }, [selectedStore]);

  useEffect(() => {
    if (from) {
      const minDate = new Date(from);
      minDate.setDate(minDate.getDate() + 1);
      if (to && new Date(to) < minDate) {
        setTo('');
      }
    }
  }, [from]);

  useEffect(() => {
    callLoadDataFromAPI({
      searchTerm: search,
      orderType,
      supplierName,
      from,
      to,
      received: received,
      sort: orderBy,
      direction: order,
      skip: page * 10,
      take: defaultTake,
    });
  }, [page, order, orderBy]);

  const loadOrderTypes = () => {
    const franchiseName = selectedStore?.franchiseName ?? '';
    getOrderTypes(franchiseName).then((result) => {
      if (result?.data) {
        setOrdersType(result.data);
      }
    })
      .catch((error) => {
        console.log(error);
      });
  };

  const loadSupplierNames = () => {
    getPurchaseOrderSuppliers(selectedStore?.franchiseName ?? '', selectedStore?.storeNumber ?? '').then((result) => {
      if (result?.data) {
        setSupplierNames(result.data);
      }
    })
      .catch((error) => {
        console.log(error);
      });
  };

  const loadAdHocReceiptCreationConfiguration = () => {
    const franchiseName = selectedStore?.franchiseName ?? '';
    const storeNumber = selectedStore?.storeNumber ?? '';

    if (!franchiseName || !storeNumber) {
      setIsAdHocReceiptCreationEnabled(false);
      return;
    }

    getConfiguration(ConfigurationName.AdHocReceiptCreationEnabled, storeNumber, franchiseName)
      .then((result) => {
        // API response shape for getConfiguration is consumed as a single configuration object in this codebase.
        const adHocConfigValue = (result as unknown as { data?: { value?: string } }).data?.value;
        setIsAdHocReceiptCreationEnabled((adHocConfigValue ?? '').toLowerCase() === 'true');
      })
      .catch((error) => {
        logError(error);
        setIsAdHocReceiptCreationEnabled(false);
      });
  };

  const openSnackbar = (message: string, severity: 'error' | 'warning' | 'info' | 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setIsSnackbarOpen(true);
  };

  const closeSnackbar = () => {
    setIsSnackbarOpen(false);
  };

  const handleCreateAdHocReceipt = async (request: CreatePurchaseOrderHeaderRequest) => {
    const storeNumber = selectedStore?.storeNumber ?? '';

    if (!storeNumber) {
      openSnackbar(t('purchaseOrderReceipt.adHocReceiptModal.validation.storeRequired'), 'error');
      return;
    }

    setIsCreatingAdHocReceipt(true);
    try {
      const response = await createAdHocPurchaseOrder(request);
      const createdId = response?.data?.id;

      if (!createdId) {
        throw new Error(t('purchaseOrderReceipt.adHocReceiptModal.errors.missingCreatedId'));
      }

      setIsCreateAdHocModalOpen(false);
      navigate(`/create-goods-receipt/${createdId}`);
    } catch (error) {
      logError(error);

      if ((error as { status?: number })?.status === 403) {
        openSnackbar(t('purchaseOrderReceipt.adHocReceiptModal.errors.forbidden'), 'error');
        return;
      }

      if ((error as { status?: number })?.status === 400) {
        const messageFromApi = extractErrorMessage(error);
        openSnackbar(messageFromApi, 'error');
        return;
      }

      openSnackbar(extractErrorMessage(error), 'error');
    } finally {
      setIsCreatingAdHocReceipt(false);
    }
  };

  const loadData = (options: PurchaseOrdersFilterOptions) => {
    setLoading(true);
    const {
      searchTerm,
      orderType,
      from,
      to,
      received,
      sort,
      direction,
      skip,
      take,
    } = options;

    setLastSearch(searchTerm);
    const storeNumber = selectedStore?.storeNumber;
    const typetest = orderType === 'All' ? '' : orderType;
    const supplier = supplierName === 'All' ? '' : supplierName;

    getPurchaseOrders(storeNumber, undefined, searchTerm, undefined, typetest, supplier, from, to, undefined, received, sort, direction, skip, take)
      .then((result) => {
        if (result?.data) {
          setPurchaseOrders(result.data);
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => { setLoading(false); });
  };

  const callLoadDataFromAPI = (options: PurchaseOrdersFilterOptions) => {
    loadData(options);
  };

  const loadDataFromOrderTable = (sort: string, direction: string, skip: number, take: number) => {
    callLoadDataFromAPI({
      searchTerm: search,
      orderType,
      supplierName,
      from,
      to,
      received,
      sort,
      direction,
      skip,
      take,
    });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleOrderTypeChange = (event: SelectChangeEvent<string>) => {
    setOrderType(event.target.value);
  };

  const handleReceivedChange = (event: SelectChangeEvent<string>) => {
    const receiptStatus = event.target.value;
    let newReceivedValue: boolean | undefined;

    if (receiptStatus === '1') {
      newReceivedValue = true;
    } else if (receiptStatus === '0') {
      newReceivedValue = false;
    } else {
      newReceivedValue = undefined;
    }
    setReceived(newReceivedValue);
  };

  const handleSupplierNameChange = (event: SelectChangeEvent<string>) => {

    const newSupplierNameValue = event.target.value;
    setSupplierName(newSupplierNameValue);
  };

  const clearAll = () => {
    setFrom('');
    setTo('');
    setSearch('');
    setReceived(undefined);
    setOrderType('All');
    setPage(0);
    setSupplierName('All');


    callLoadDataFromAPI({
      searchTerm: '',
      orderType: '',
      supplierName: '',
      from: '',
      to: '',
      received: undefined,
      sort: defaultSort,
      direction: defaultDirection,
      skip: 0,
      take: 10,
    });

    const receivedSelect = document.getElementById('received-select') as HTMLSelectElement | null;
    if (receivedSelect) {
      receivedSelect.value = '';
    }

    const orderType = document.getElementById('order-type') as HTMLSelectElement | null;
    if (orderType) {
      orderType.value = '';
    }

    const supplierName = document.getElementById('supplier-name') as HTMLSelectElement | null;
    if (supplierName) {
      supplierName.value = '';
    }
  };

  const searchPurchaseOrders = () => {
    setPage(0);

    callLoadDataFromAPI({
      searchTerm: search,
      orderType,
      supplierName,
      from,
      to,
      received: received,
      sort: orderBy,
      direction: order,
      skip: 0,
      take: defaultTake,
    });
  };

  const isAsc = order === 'asc';

  const handleSort = (property: string) => {
    const order = isAsc ? 'desc' : 'asc';
    setOrder(order);
    setOrderBy(property);
    loadDataFromOrderTable(property, order, page * 10, 10);
  };

  return <>
    <Grid container
      spacing={5}>
      <Grid item
        lg={4}
        sm={8}
        xs={12} >
        <FormControl fullWidth
          size='small'>
          <TextField
            id="input-with-icon-textfield"
            label={t('purchaseOrderReceipt.filterForm.searchTitle')}
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SvgIcon><SearchSm /></SvgIcon>
                </InputAdornment>
              ),
            }}
            value={search}
            onChange={handleSearchChange}
            onKeyDown={e => handleSearchFieldKeyDown(e, searchPurchaseOrders)}
          />
        </FormControl>
      </Grid>
      <Grid item
        lg={4}
        sm={4}
        xs={6} >
        <FormControl fullWidth
          size='small'>
          <InputLabel id="received-select">{t('purchaseOrderReceipt.filterForm.labelReceiptStatus')}</InputLabel>
          <Select
            value={received ? '1' : received === false ? '0' : ''}
            label={t('purchaseOrderReceipt.filterForm.labelReceiptStatus')}
            onChange={handleReceivedChange}
          >
            <MenuItem value={'1'}>{t('purchaseOrderReceipt.filterForm.statusReceived')}</MenuItem>
            <MenuItem value={'0'}>{t('purchaseOrderReceipt.filterForm.statusNotReceived')}</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item
        lg={4}
        sm={4}
        xs={6} >
        <FormControl fullWidth
          size='small'>
          <InputLabel id="type-label">{t('purchaseOrderReceipt.filterForm.labelOrderType')}</InputLabel>
          <Select
            id='order-type'
            value={orderType}
            onChange={handleOrderTypeChange}
            label={t('purchaseOrderReceipt.filterForm.labelOrderType')}
          >
            <MenuItem value="All">
              All
            </MenuItem>
            {ordersType?.name?.map((name) => (
              <MenuItem key={name}
                value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item
        lg={4}
        md={4}
        xs={6} >
        <FormControl fullWidth
          size='small'>
          <InputLabel id="type-label">{t('purchaseOrderReceipt.filterForm.labelSupplierName')}</InputLabel>
          <Select
            id='supplier-name'
            value={supplierName}
            onChange={handleSupplierNameChange}
            label={t('purchaseOrderReceipt.filterForm.labelSupplierName')}
          >
            <MenuItem value="All">
              All
            </MenuItem>
            {supplierNames?.supplierName?.map((supplierName) => (
              <MenuItem key={supplierName}
                value={supplierName}>
                {supplierName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item
        lg={4}
        sm={4}
        xs={6} >
        <DatePickerLocalized
          useCalendar={false}
          label={t('purchaseOrderReceipt.filterForm.labelReceiptDateFrom')}
          value={from}
          translations={{
            clearButtonLabel: t('calendar.clearButtonLabel'),
            todayButtonLabel: t('calendar.todayButtonLabel'),
            cancelButtonLabel: t('calendar.cancelButtonLabel'),
            okButtonLabel: t('calendar.okButtonLabel'),
          }}
          onChange={(newValue) => {
            if (newValue) {
              setFrom(newValue);
            }
          }}
        />
      </Grid>
      <Grid item
        lg={4}
        sm={4}
        xs={6} >
        <DatePickerLocalized
          useCalendar={false}
          label={t('purchaseOrderReceipt.filterForm.labelReceiptDateTo')}
          value={to}
          translations={{
            clearButtonLabel: t('calendar.clearButtonLabel'),
            todayButtonLabel: t('calendar.todayButtonLabel'),
            cancelButtonLabel: t('calendar.cancelButtonLabel'),
            okButtonLabel: t('calendar.okButtonLabel'),
          }}
          onChange={(newValue) => {
            if (newValue === '') {
              setTo('');
            } else if (newValue) {
              const minDate = new Date(from);
              minDate.setDate(minDate.getDate() + 1);
              if (new Date(newValue) < minDate) {
                setTo('');
              } else {
                setTo(newValue);
              }
            }
          }}
        />
      </Grid>
      <Grid item
        xs={12}
        md={2} >
        <Button
          variant="secondary"
          size="lg"
          onClick={clearAll}
          fullWidth>
          {t('clearAll')}
        </Button>
      </Grid>
      <Grid item
        xs={12}
        md={2} >
        <Button
          variant="primary"
          size="lg"
          onClick={searchPurchaseOrders}
          startIcon={<SearchSm />}
          fullWidth
        >
          {t('search')}
        </Button>
      </Grid>
      <Grid item
        xs={12}>
        <Section>
          <PurchaseOrdersTable
            list={purchaseOrders}
            setPageZero={page}
            loadData={loadDataFromOrderTable}
            order={order}
            orderBy={orderBy}
            loading={loading}
            handleSort={handleSort}
            page={page}
            setPage={setPage}
            search={lastSearch}
            clearAll={clearAll}
            canCreateAdHocReceipt={canCreateAdHocReceipt}
            onCreateAdHocReceipt={() => setIsCreateAdHocModalOpen(true)} />
        </Section>
      </Grid>
    </Grid>

    {canCreateAdHocReceipt && <CreateAdHocReceiptModal
      open={isCreateAdHocModalOpen}
      isSubmitting={isCreatingAdHocReceipt}
      storeNumber={selectedStore?.storeNumber ?? ''}
      onClose={() => setIsCreateAdHocModalOpen(false)}
      onSubmit={handleCreateAdHocReceipt} />}

    <MessagesSnackbar
      open={isSnackbarOpen}
      onClose={closeSnackbar}
      message={snackbarMessage}
      severity={snackbarSeverity}
      duration={5000}
    />
  </>;
};

export default PurchaseOrderBox;